<template>
  <div class="px-6 py-6" v-if="feedback">
    <!-- Back button -->
    <button
      @click="uiStore.setCurrentPage('feedback')"
      class="text-sm text-indigo-600 hover:text-indigo-800"
    >
      &larr; Back to list
    </button>

    <div class="grid grid-cols-1 gap-6 pt-4 lg:grid-cols-3">
      <!-- Main content -->
      <div class="lg:col-span-2 flex flex-col gap-4">
        <!-- Summary -->
        <div class="bg-white rounded-lg border border-gray-200 px-5 py-4">
          <div class="flex items-start gap-3">
            <span
              class="inline-flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold text-white flex-shrink-0"
              :class="getPriorityColor(feedback.priorityScore)"
            >
              {{ feedback.priorityScore }}
            </span>
            <div class="flex-1">
              <h2 class="text-base font-medium text-gray-900">
                {{ feedback.aiSummary || 'No AI summary yet' }}
              </h2>
              <div class="flex items-center gap-3 pt-1">
                <span
                  v-if="feedback.type"
                  class="text-xs px-2 py-0.5 rounded-full"
                  :class="getTypeClass(feedback.type)"
                >
                  {{ feedback.type.replace(/_/g, ' ') }}
                </span>
                <span
                  v-if="feedback.kanoCategory"
                  class="text-xs text-gray-500"
                >
                  Kano: {{ feedback.kanoCategory.replace(/_/g, ' ') }}
                </span>
                <span
                  v-if="feedback.sentiment"
                  class="text-xs"
                  :class="{
                    'text-red-500': feedback.sentiment === 'frustrated',
                    'text-gray-400': feedback.sentiment === 'neutral',
                    'text-green-500': feedback.sentiment === 'positive',
                  }"
                >
                  {{ feedback.sentiment }}
                </span>
                <span
                  v-if="feedback.urgency"
                  class="text-xs font-medium"
                  :class="{
                    'text-red-600': feedback.urgency === 'critical',
                    'text-orange-600': feedback.urgency === 'high',
                    'text-yellow-600': feedback.urgency === 'medium',
                    'text-gray-500': feedback.urgency === 'low',
                  }"
                >
                  {{ feedback.urgency }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Audio Player -->
        <div v-if="feedback.audioUrl" class="feedback-detail-audio bg-white rounded-lg border border-gray-200 px-5 py-4">
          <h3 class="text-sm font-medium text-gray-700 pb-2">Audio Recording</h3>
          <AudioPlayer :src="feedback.audioUrl" />
        </div>

        <!-- Screenshots -->
        <div v-if="feedback.screenshotUrls && feedback.screenshotUrls.length > 0" class="feedback-detail-screenshots bg-white rounded-lg border border-gray-200 px-5 py-4">
          <h3 class="text-sm font-medium text-gray-700 pb-2">Screenshots ({{ feedback.screenshotUrls.length }})</h3>
          <ScreenshotGallery :urls="feedback.screenshotUrls" />
        </div>

        <!-- Transcript -->
        <div class="bg-white rounded-lg border border-gray-200 px-5 py-4">
          <h3 class="text-sm font-medium text-gray-700">Transcript</h3>
          <p class="text-sm text-gray-800 pt-2 whitespace-pre-wrap leading-relaxed">
            {{ feedback.transcript }}
          </p>
        </div>

        <!-- Context -->
        <div class="bg-white rounded-lg border border-gray-200 px-5 py-4">
          <h3 class="text-sm font-medium text-gray-700">Context</h3>
          <div class="grid grid-cols-2 gap-x-6 gap-y-2 pt-2 text-sm">
            <div>
              <span class="text-gray-500">Page:</span>
              <span class="text-gray-800"> {{ feedback.pageUrl }}</span>
            </div>
            <div>
              <span class="text-gray-500">Title:</span>
              <span class="text-gray-800"> {{ feedback.pageTitle }}</span>
            </div>
            <div>
              <span class="text-gray-500">Browser:</span>
              <span class="text-gray-800"> {{ feedback.browserInfo }}</span>
            </div>
            <div>
              <span class="text-gray-500">Device:</span>
              <span class="text-gray-800"> {{ feedback.deviceType }}</span>
            </div>
            <div>
              <span class="text-gray-500">Resolution:</span>
              <span class="text-gray-800"> {{ feedback.screenResolution }}</span>
            </div>
            <div>
              <span class="text-gray-500">Session:</span>
              <span class="text-gray-800"> {{ Math.round(feedback.sessionDuration / 60) }}m</span>
            </div>
          </div>
        </div>

        <!-- Notes -->
        <div class="bg-white rounded-lg border border-gray-200 px-5 py-4">
          <h3 class="text-sm font-medium text-gray-700">Internal Notes</h3>
          <div class="flex flex-col gap-2 pt-2" v-if="notes && notes.length > 0">
            <div
              v-for="note in notes"
              :key="note.id"
              class="bg-gray-50 rounded-md px-3 py-2"
            >
              <p class="text-sm text-gray-800">{{ note.content }}</p>
              <p class="text-xs text-gray-400 pt-1">
                {{ note.authorName }} · {{ formatDate(note.createdAt) }}
              </p>
            </div>
          </div>
          <p v-else class="text-xs text-gray-400 pt-2">No notes yet</p>

          <div class="flex gap-2 pt-3">
            <input
              v-model="newNote"
              @keyup.enter="addNote"
              placeholder="Add a note..."
              class="flex-1 text-sm border border-gray-300 rounded-md px-3 py-1.5"
            />
            <button
              @click="addNote"
              :disabled="!newNote.trim()"
              class="text-sm px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </div>

        <!-- Activity Log -->
        <div class="bg-white rounded-lg border border-gray-200 px-5 py-4">
          <h3 class="text-sm font-medium text-gray-700">Activity</h3>
          <div class="flex flex-col gap-1 pt-2" v-if="activityLog && activityLog.length > 0">
            <div
              v-for="entry in activityLog"
              :key="entry.id"
              class="text-xs text-gray-500 py-1"
            >
              <span class="font-medium text-gray-700">{{ entry.actorName }}</span>
              {{ formatAction(entry.action) }}
              <span v-if="entry.details?.newStatus" class="font-medium">
                → {{ entry.details.newStatus }}
              </span>
              · {{ formatDate(entry.createdAt) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="flex flex-col gap-4">
        <!-- Status -->
        <div class="bg-white rounded-lg border border-gray-200 px-5 py-4">
          <h3 class="text-sm font-medium text-gray-700">Status</h3>
          <select
            :value="feedback.status"
            @change="changeStatus(($event.target as HTMLSelectElement).value)"
            class="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 mt-2"
          >
            <option value="new">New</option>
            <option value="investigating">Investigating</option>
            <option value="planned">Planned</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="wont_fix">Won't Fix</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        <!-- Assignment -->
        <div class="bg-white rounded-lg border border-gray-200 px-5 py-4">
          <h3 class="text-sm font-medium text-gray-700">Assigned To</h3>
          <select
            :value="feedback.assignedToUserId || ''"
            @change="assignMember(($event.target as HTMLSelectElement).value)"
            class="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 mt-2"
          >
            <option value="">Unassigned</option>
            <option
              v-for="member in teamMembers"
              :key="member.userId"
              :value="member.userId"
            >
              {{ member.name }}
            </option>
          </select>
        </div>

        <!-- AI Actions -->
        <div class="bg-white rounded-lg border border-gray-200 px-5 py-4">
          <h3 class="text-sm font-medium text-gray-700">AI Analysis</h3>
          <div class="flex flex-col gap-2 pt-2">
            <p class="text-xs text-gray-500" v-if="feedback.analysisCompletedAt">
              Analyzed: {{ new Date(feedback.analysisCompletedAt).toLocaleString() }}
            </p>
            <p class="text-xs text-gray-500" v-if="feedback.aiConfidence">
              Confidence: {{ Math.round(feedback.aiConfidence * 100) }}%
            </p>
            <button
              @click="triggerReanalyze"
              :disabled="reanalyzing"
              class="text-sm px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {{ reanalyzing ? 'Re-analyzing...' : 'Re-analyze with AI' }}
            </button>
          </div>
        </div>

        <!-- User Info -->
        <div class="bg-white rounded-lg border border-gray-200 px-5 py-4">
          <h3 class="text-sm font-medium text-gray-700">User</h3>
          <div class="flex flex-col gap-1 pt-2 text-sm">
            <p class="text-gray-800">{{ feedback.userEmail }}</p>
            <p class="text-xs text-gray-500" v-if="feedback.userSubscriptionTier">
              Tier: {{ feedback.userSubscriptionTier }}
            </p>
            <p class="text-xs text-gray-500">
              Previous feedback: {{ feedback.userPreviousFeedbackCount }}
            </p>
          </div>
        </div>

        <!-- Priority Breakdown -->
        <div class="bg-white rounded-lg border border-gray-200 px-5 py-4" v-if="feedback.priorityBreakdown">
          <h3 class="text-sm font-medium text-gray-700">Priority Breakdown</h3>
          <div class="flex flex-col gap-1 pt-2 text-xs">
            <div class="flex items-center gap-2">
              <span class="text-gray-500 w-20">Kano</span>
              <span class="text-gray-800">{{ feedback.priorityBreakdown.kanoScore }}</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-gray-500 w-20">Frequency</span>
              <span class="text-gray-800">{{ feedback.priorityBreakdown.frequencyScore }}</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-gray-500 w-20">User Value</span>
              <span class="text-gray-800">{{ feedback.priorityBreakdown.userValueScore }}</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-gray-500 w-20">Journey</span>
              <span class="text-gray-800">{{ feedback.priorityBreakdown.journeyScore }}</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-gray-500 w-20">Recency</span>
              <span class="text-gray-800">{{ feedback.priorityBreakdown.recencyScore }}</span>
            </div>
          </div>
        </div>

        <!-- Timestamps -->
        <div class="bg-white rounded-lg border border-gray-200 px-5 py-4">
          <h3 class="text-sm font-medium text-gray-700">Timestamps</h3>
          <div class="flex flex-col gap-1 pt-2 text-xs text-gray-500">
            <p>Created: {{ new Date(feedback.createdAt).toLocaleString() }}</p>
            <p>Updated: {{ new Date(feedback.updatedAt).toLocaleString() }}</p>
            <p v-if="feedback.assignedAt">Assigned: {{ new Date(feedback.assignedAt).toLocaleString() }}</p>
            <p v-if="feedback.resolvedAt">Resolved: {{ new Date(feedback.resolvedAt).toLocaleString() }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="px-6 py-6 text-sm text-gray-500">Loading feedback detail...</div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import apiClient from '../../api/client'
import { useUIStore } from '../../stores/uiStore'
import AudioPlayer from '../../components/admin/AudioPlayer.vue'
import ScreenshotGallery from '../../components/admin/ScreenshotGallery.vue'

const props = defineProps<{
  feedbackId: number
}>()

const uiStore = useUIStore()
const newNote = ref('')
const reanalyzing = ref(false)

const feedback = ref<any>(null)
const notes = ref<any[]>([])
const activityLog = ref<any[]>([])
const teamMembers = ref<any[]>([])

async function fetchAll() {
  if (!props.feedbackId) return
  const [feedbackData, notesData, activityData, teamData] = await Promise.all([
    apiClient.feedback.get(props.feedbackId),
    apiClient.notes.list(props.feedbackId),
    apiClient.feedback.getActivity(props.feedbackId),
    apiClient.teamMembers.list(),
  ])
  feedback.value = feedbackData
  notes.value = notesData
  activityLog.value = activityData
  teamMembers.value = teamData
}

onMounted(fetchAll)

watch(() => props.feedbackId, fetchAll)

async function changeStatus(newStatus: string) {
  await apiClient.feedback.updateStatus(props.feedbackId, {
    status: newStatus,
    actorUserId: 'admin-001',
    actorName: 'Admin',
  })
  uiStore.showNotification(`Status changed to ${newStatus}`, 'success')
  feedback.value = await apiClient.feedback.get(props.feedbackId)
  activityLog.value = await apiClient.feedback.getActivity(props.feedbackId)
}

async function assignMember(userId: string) {
  if (userId) {
    await apiClient.feedback.assign(props.feedbackId, {
      assigneeId: userId,
      actorUserId: 'admin-001',
      actorName: 'Admin',
    })
    uiStore.showNotification('Assigned', 'success')
    feedback.value = await apiClient.feedback.get(props.feedbackId)
    activityLog.value = await apiClient.feedback.getActivity(props.feedbackId)
  }
}

async function triggerReanalyze() {
  reanalyzing.value = true
  try {
    await apiClient.analysis.reanalyze(props.feedbackId)
    uiStore.showNotification('Re-analysis scheduled', 'success')
  } catch (_err) {
    uiStore.showNotification('Re-analysis failed', 'error')
  }
  setTimeout(() => { reanalyzing.value = false }, 3000)
}

async function addNote() {
  if (!newNote.value.trim()) return
  await apiClient.notes.create(props.feedbackId, {
    authorUserId: 'admin-001',
    authorName: 'Admin',
    content: newNote.value.trim(),
  })
  newNote.value = ''
  uiStore.showNotification('Note added', 'success')
  notes.value = await apiClient.notes.list(props.feedbackId)
  activityLog.value = await apiClient.feedback.getActivity(props.feedbackId)
}

function getPriorityColor(score: number): string {
  if (score >= 80) return 'bg-red-600'
  if (score >= 60) return 'bg-orange-500'
  if (score >= 40) return 'bg-yellow-500'
  if (score >= 20) return 'bg-blue-500'
  return 'bg-gray-400'
}

function getTypeClass(type: string): string {
  const classes: Record<string, string> = {
    bug: 'bg-red-100 text-red-700',
    feature_request: 'bg-indigo-100 text-indigo-700',
    improvement: 'bg-teal-100 text-teal-700',
    support: 'bg-amber-100 text-amber-700',
    praise: 'bg-green-100 text-green-700',
    confusion: 'bg-orange-100 text-orange-700',
  }
  return classes[type] || 'bg-gray-100 text-gray-600'
}

function formatAction(action: string): string {
  const labels: Record<string, string> = {
    created: 'created this feedback',
    status_changed: 'changed status',
    assigned: 'assigned this',
    unassigned: 'unassigned this',
    priority_changed: 'changed priority',
    note_added: 'added a note',
    merged: 'merged feedback',
    archived: 'archived this',
    restored: 'restored this',
  }
  return labels[action] || action
}

function formatDate(timestamp: number): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const hours = Math.floor(diff / 3600000)
  if (hours < 1) return 'just now'
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString()
}
</script>
